// A finger never lands perfectly still: a tap drifts a few pixels between press and
// release, and Chrome's own touch slop is 8px. Below this the gesture was a tap, above it
// the visitor was panning and did not mean to toggle the zoom. Without it every tap on a
// ZOOMED image counted as a pan, so tap-to-zoom-out did nothing on a phone and the top-bar
// button was the only way back out. (Mouse clicks report identical press/release
// coordinates, which is why this only ever showed up on touch.)
const TAP_SLOP = 8;

// <product-lightbox>: dependency-free fullscreen image viewer for the PDP.
// Public API: openTo(mediaId) opens at that image; close() closes.
// Slides come from a <script type="application/json" data-lb-slides> array of
// { id, src, alt }. Zoom/pan and share are layered on in later tasks.
customElements.define(
  "product-lightbox",
  class extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector("[data-lb-slides]");
      try { this.slides = JSON.parse(json.textContent); } catch (e) { this.slides = []; }
      this.img = this.querySelector("[data-lb-image]");
      this.stage = this.querySelector("[data-lb-stage]");
      this.counter = this.querySelector("[data-lb-counter]");
      this.rail = this.querySelector("[data-lb-rail]");
      this.toastEl = this.querySelector("[data-lb-toast]");
      this.index = 0;
      this.pointers = new Map();
      this.resetZoomState();

      this.addEventListener("click", (e) => this.onClick(e));
      // Dismissal is decided from where the press landed, not from the click's target: a
      // click resolves to the common ancestor of press and release, so a drag that began
      // on the image but finished over the backdrop arrives as a backdrop click.
      this.addEventListener("pointerdown", (e) => {
        this._downOnBackdrop = e.target === this.stage || e.target === this;
      }, true);
      this.stage.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
      this.stage.addEventListener("pointerdown", (e) => this.onPointerDown(e));
      // Tracked on the window, not the stage: a pan has to keep following a pointer that
      // runs past the stage edge. setPointerCapture is the usual way to get that, but it
      // retargets the compatibility click that follows the gesture onto the capture
      // element -- so a click that began on the image arrived as a click on the stage,
      // which the routing in onClick then read as a backdrop click and closed on.
      window.addEventListener("pointermove", (e) => this.onPointerMove(e));
      window.addEventListener("pointerup", (e) => this.onPointerUp(e));
      window.addEventListener("pointercancel", (e) => this.onPointerUp(e));
      this._onKey = (e) => this.onKey(e);
    }

    // ---- open / close ----
    openTo(mediaId) {
      const i = this.slides.findIndex((s) => String(s.id) === String(mediaId));
      if (i < 0) return;
      this._returnFocus = document.activeElement;
      this.classList.add("is-open");
      this.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this._onKey);
      this.goTo(i);
      this._release =
        window.theme && window.theme.trapFocus ? window.theme.trapFocus(this) : null;
    }
    close() {
      this.classList.remove("is-open");
      this.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this._onKey);
      if (this._release) { this._release(); this._release = null; }
      if (this._returnFocus && this._returnFocus.focus) this._returnFocus.focus();
    }

    // ---- navigation ----
    goTo(i) {
      const n = this.slides.length;
      if (!n) return;
      this.index = (i + n) % n;
      const slide = this.slides[this.index];
      if (slide.w) this.img.width = slide.w;
      if (slide.h) this.img.height = slide.h;
      this.img.src = slide.src;
      this.img.alt = slide.alt || "";
      this.resetZoom();
      if (this.counter) this.counter.textContent = this.index + 1 + " / " + n;
      if (this.rail) {
        this.rail.querySelectorAll("[data-lb-index]").forEach((t) => {
          const active = Number(t.dataset.lbIndex) === this.index;
          t.classList.toggle("is-active", active);
          if (active) t.scrollIntoView({ block: "nearest", inline: "center" });
        });
      }
      this.preload(this.index + 1);
      this.preload(this.index - 1);
    }
    next() { this.goTo(this.index + 1); }
    prev() { this.goTo(this.index - 1); }
    preload(i) {
      const n = this.slides.length;
      if (!n) return;
      const s = this.slides[((i % n) + n) % n];
      if (s) { const im = new Image(); im.src = s.src; }
    }

    // ---- click routing ----
    onClick(e) {
      if (e.target.closest("[data-lb-close]")) return this.close();
      if (e.target.closest("[data-lb-prev]")) return this.prev();
      if (e.target.closest("[data-lb-next]")) return this.next();
      if (e.target.closest("[data-lb-zoom-in]")) return this.zoomStep(1);
      if (e.target.closest("[data-lb-zoom-out]")) return this.zoomStep(-1);
      if (e.target.closest("[data-lb-zoom-reset]")) return this.resetZoom();
      if (e.target.closest("[data-lb-share]")) return this.share();
      const thumb = e.target.closest("[data-lb-index]");
      if (thumb) return this.goTo(Number(thumb.dataset.lbIndex));
      if (e.target === this.img) {
        if (!this._dragged) this.toggleZoom(e.clientX, e.clientY);
        return;
      }
      // Backdrop / empty stage area (not the image, not a control) closes -- but only when
      // the press landed there too, and never as the tail of a pan or pinch, which can
      // start on the backdrop while zoomed and still be a deliberate gesture.
      if (this._dragged || !this._downOnBackdrop) return;
      if (e.target === this.stage || e.target === this) this.close();
    }

    // ---- keyboard ----
    onKey(e) {
      if (!this.classList.contains("is-open")) return;
      if (e.key === "Escape") this.close();
      else if (e.key === "ArrowRight") this.next();
      else if (e.key === "ArrowLeft") this.prev();
      else if (e.key === "+" || e.key === "=") this.zoomStep(1);
      else if (e.key === "-") this.zoomStep(-1);
      else if (e.key === "0") this.resetZoom();
    }

    // ---- zoom / pan ----
    resetZoomState() { this.scale = 1; this.tx = 0; this.ty = 0; }
    resetZoom() { this.resetZoomState(); this.applyTransform(); }
    applyTransform() {
      this.img.style.transform =
        "translate(" + this.tx + "px," + this.ty + "px) scale(" + this.scale + ")";
      this.classList.toggle("is-zoomed", this.scale > 1);
    }
    clamp() {
      const sw = this.img.clientWidth * this.scale;
      const sh = this.img.clientHeight * this.scale;
      const maxX = Math.max(0, (sw - this.stage.clientWidth) / 2);
      const maxY = Math.max(0, (sh - this.stage.clientHeight) / 2);
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }
    zoomAt(newScale, clientX, clientY) {
      newScale = Math.min(4, Math.max(1, newScale));
      const rect = this.stage.getBoundingClientRect();
      const cx = clientX - (rect.left + rect.width / 2);
      const cy = clientY - (rect.top + rect.height / 2);
      // Keep the content point under the cursor fixed as scale changes.
      this.tx = cx - ((cx - this.tx) / this.scale) * newScale;
      this.ty = cy - ((cy - this.ty) / this.scale) * newScale;
      this.scale = newScale;
      if (this.scale === 1) this.resetZoomState();
      this.clamp();
      this.applyTransform();
    }
    zoomStep(dir) {
      const c = this.stage.getBoundingClientRect();
      this.zoomAt(this.scale + dir * 0.5, c.left + c.width / 2, c.top + c.height / 2);
    }
    toggleZoom(clientX, clientY) {
      if (this.scale > 1) this.resetZoom();
      else this.zoomAt(2, clientX, clientY);
    }
    onWheel(e) {
      if (!this.classList.contains("is-open")) return;
      e.preventDefault();
      this.zoomAt(this.scale - Math.sign(e.deltaY) * 0.25, e.clientX, e.clientY);
    }
    pinchDist() {
      const p = [...this.pointers.values()];
      return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
    }
    onPointerDown(e) {
      if (e.target.closest("button")) return; // let prev/next behave as buttons
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this._dragged = false;
      // Measured from where the press landed, not from the previous move, so slow drift
      // accumulates instead of resetting every event.
      this._startX = e.clientX;
      this._startY = e.clientY;
      if (this.pointers.size === 1) this._multi = false;
      if (this.pointers.size === 2) {
        this._multi = true;
        this._pinchStart = this.pinchDist();
        this._scaleStart = this.scale;
      } else if (this.scale > 1) {
        this._panning = true;
        this._lastX = e.clientX;
        this._lastY = e.clientY;
      }
    }
    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2 && this._pinchStart) {
        const pts = [...this.pointers.values()];
        const mx = (pts[0].x + pts[1].x) / 2;
        const my = (pts[0].y + pts[1].y) / 2;
        this._dragged = true;
        this.zoomAt(this._scaleStart * (this.pinchDist() / this._pinchStart), mx, my);
      } else if (this._panning) {
        this.tx += e.clientX - this._lastX;
        this.ty += e.clientY - this._lastY;
        this._lastX = e.clientX;
        this._lastY = e.clientY;
        // The pan itself follows the finger from the first pixel -- only the "this was a
        // drag, so swallow the click" verdict waits for the slop.
        if (this.travel(e) > TAP_SLOP) this._dragged = true;
        this.clamp();
        this.applyTransform();
      }
    }
    // Distance from where this gesture's press landed.
    travel(e) {
      return Math.hypot(e.clientX - this._startX, e.clientY - this._startY);
    }
    onPointerUp(e) {
      const tracked = this.pointers.has(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this._pinchStart = null;
      if (this.pointers.size === 0) this._panning = false;
      // Only the last finger of a gesture this element was actually tracking, and never
      // the tail of a pinch -- fingers leave a pinch one at a time, and the second one
      // lifting off is not a flick.
      if (tracked && this.pointers.size === 0 && !this._multi) this.maybeSwipe(e);
    }

    // ---- swipe between images ----
    // A horizontal flick at fit scale changes image. On a phone this is the primary way
    // through the gallery: the chevrons are 46px targets in the corners of a fullscreen
    // viewer and the thumbnails are smaller still, so a viewer that only answered to them
    // read as stuck on the first image.
    maybeSwipe(e) {
      // Zoomed, the same gesture is a pan -- stealing it would make the zoom unusable.
      if (this.scale > 1 || this.slides.length < 2) return;
      const dx = e.clientX - this._startX;
      const dy = e.clientY - this._startY;
      // Horizontal-dominant, so a vertical drag stays available to whatever it means
      // later (and never turns a scroll attempt into an image change).
      if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < this.swipeMin()) return;
      // The click that follows a swipe must not also toggle the zoom: at fit scale
      // nothing else in this gesture sets the flag, because there was no pan to flag.
      this._dragged = true;
      if (dx < 0) this.next();
      else this.prev();
    }
    // A share of the stage rather than a fixed pixel count, floored so a small stage
    // still needs a deliberate flick: ~58px on a 390px phone, well past TAP_SLOP.
    swipeMin() {
      return Math.max(40, this.stage.clientWidth * 0.15);
    }

    // ---- share (current image's deep-link) ----
    share() {
      const slide = this.slides[this.index];
      let url = this.dataset.shareUrl;
      try {
        const u = new URL(this.dataset.shareUrl, location.href);
        if (slide) u.searchParams.set("img", slide.id);
        url = u.href;
      } catch (e) {}
      const data = { title: this.dataset.shareTitle, url: url };
      if (navigator.share) {
        navigator.share(data).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => this.showToast()).catch(function () {});
      } else {
        this.showToast();
      }
    }
    showToast() {
      if (!this.toastEl) return;
      this.toastEl.classList.add("is-visible");
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => this.toastEl.classList.remove("is-visible"), 2000);
    }
  }
);
